// PHASE_1 / B0 — client-convenience authorization gate (ADR-017; [D6]).
//
// ⚠ CLIENT CONVENIENCE ONLY — THE SERVER IS THE AUTHORITY. ScopeGate hides
// actions the caller's resolved scope/role cannot perform so the UI doesn't
// dangle buttons that would 403. It is NEVER a security boundary: every route
// independently enforces scope-tier + role×module×action server-side (ADR-017,
// gap §5.11). Removing this component must not change what a caller can
// actually DO — only what they SEE. The review gate verifies the route denies
// regardless (PHASE_1 review gate §2).
//
// The host passes the caller's `allowed` capability set (resolved from auth
// claims — real role resolution lands in P5; P0/P1 thread a convenience set)
// and the `required` capability for the gated action. When required ∈ allowed,
// children render; otherwise the optional `fallback` renders (default:
// nothing). Default-closed: an unknown/absent capability hides the action.

import { type ReactNode } from "react";

export interface ScopeGateProps {
  /**
   * The capability this action requires (e.g. "leave.approve"). Default-closed:
   * if it is not present in `allowed`, the children are hidden.
   */
  readonly required: string;
  /**
   * The caller's resolved capability set. A `Set` or array of capability
   * strings the host derived from auth claims. Convenience metadata only —
   * the server re-checks every request (ADR-017).
   */
  readonly allowed: ReadonlySet<string> | readonly string[];
  /** Rendered when the gate is OPEN (caller has the capability). */
  readonly children: ReactNode;
  /** Rendered when the gate is CLOSED. Defaults to nothing. */
  readonly fallback?: ReactNode;
}

function has(
  allowed: ReadonlySet<string> | readonly string[],
  required: string,
): boolean {
  // Normalize both shapes to a Set membership test — TS does not reliably
  // narrow the `ReadonlySet | readonly[]` union through instanceof/isArray,
  // and a Set lookup is O(1) regardless of the input shape.
  const set = allowed instanceof Set ? allowed : new Set<string>(allowed);
  return set.has(required);
}

/**
 * Render `children` only when the caller's `allowed` set contains `required`;
 * otherwise render `fallback` (default nothing). Convenience-only — see the
 * file header: the server independently authorizes every action.
 */
export function ScopeGate({
  required,
  allowed,
  children,
  fallback = null,
}: ScopeGateProps): React.ReactElement {
  return <>{has(allowed, required) ? children : fallback}</>;
}
